/**
 * Derived views of the item graph (decision 0035): stages of a container, the overdue alarm's candidates and a
 * person's My work. Pure, never stored; the web and the Project DO compute the same thing.
 */

import { type Frontier, holds, readiness, waitingOn } from "./readiness"
import { isOpenItem, isRunEnded, must } from "./state"
import { childrenOf, isContainer } from "./tree"
import type { Actor, Item, MyWorkRow, ProjectState, UserId } from "./types"

const DAY = 86_400_000

const byRank = (a: Item, b: Item): number =>
  a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0

/**
 * A container's children layered by `blocks` among siblings (longest path): stage 1 runs "at the same time", stage 2
 * "then …". Cancelled children are left out; within a stage, rank order.
 */
export const stagesOf = (
  s: ProjectState,
  container: Pick<Item, "id">,
): ReadonlyArray<ReadonlyArray<Item>> => {
  const kids = childrenOf(s, container.id).filter((k) => k.status !== "cancelled")
  const ids = new Set<string>(kids.map((k) => k.id))
  const preds = new Map<string, ReadonlyArray<string>>(
    kids.map((k) => [
      k.id,
      s.edges
        .filter((e) => e.kind === "blocks" && e.to.id === k.id && ids.has(e.from.id))
        .map((e) => e.from.id),
    ]),
  )
  const level = new Map<string, number>()
  const levelOf = (id: string, seen: ReadonlySet<string>): number => {
    const known = level.get(id)
    if (known !== undefined) return known
    if (seen.has(id)) return 0
    const next = new Set(seen).add(id)
    const ps = preds.get(id) ?? []
    const l = ps.length === 0 ? 0 : 1 + Math.max(...ps.map((p) => levelOf(p, next)))
    level.set(id, l)
    return l
  }
  for (const k of kids) levelOf(k.id, new Set())
  const out: Item[][] = []
  for (const k of [...kids].sort(byRank)) {
    const l = level.get(k.id) ?? 0
    while (out.length <= l) out.push([])
    must(out[l], "stage").push(k)
  }
  return out
}

/**
 * The overdue alarm's work list (G6): open items with a person assignee, due at or before `now`, holding others, not
 * yet noticed for this due date.
 */
export const overdueCandidates = (s: ProjectState, now: number): ReadonlyArray<Item> =>
  s.items.filter(
    (t) =>
      isOpenItem(t) &&
      t.assignee?._tag === "user" &&
      t.dueAt !== null &&
      t.dueAt <= now &&
      t.overdueNoticed !== t.dueAt &&
      holds(s, t, now).length > 0,
  )

/** The earliest future due date that would make an item an overdue candidate (for the alarm). */
export const nextOverdueAt = (s: ProjectState, now: number): number | null => {
  let min: number | null = null
  for (const t of s.items)
    if (
      isOpenItem(t) &&
      t.assignee?._tag === "user" &&
      t.dueAt !== null &&
      t.dueAt > now &&
      t.overdueNoticed !== t.dueAt &&
      (min === null || t.dueAt < min)
    )
      min = t.dueAt
  return min
}

/**
 * A name for an actor in a sentence: a member's display name, an agent's @handle, a worker's W-n with its agent
 * ("W-31 (worker of @guy)": decision 0158, two workers' messages read "from @guy to @guy"), else "Minga".
 */
export const actorName = (s: ProjectState, a: Actor): string => {
  if (a._tag === "user") return s.members.find((m) => m.userId === a.userId)?.displayName ?? "someone"
  if (a._tag === "agent" || a._tag === "worker") {
    const handle = `@${s.agents.find((x) => x.id === a.agentId)?.handle ?? "agent"}`
    if (a._tag === "agent") return handle
    const run = s.runs.find((r) => r.id === a.runId)
    return `${run?.worker?.ref ?? run?.ref ?? "a worker"} (worker of ${handle})`
  }
  return "Minga"
}

/** "waiting on Jannik · DIC-3, DIC-4; @lea · DIC-9": the frontier in words. */
export const frontierText = (s: ProjectState, frontier: ReadonlyArray<Frontier>): string | null => {
  if (frontier.length === 0) return null
  const groups = new Map<string, string[]>()
  const add = (who: string, ref: string | null) => {
    const list = groups.get(who) ?? []
    if (ref) list.push(ref)
    groups.set(who, list)
  }
  for (const f of frontier) {
    const item = f.itemId ? s.items.find((t) => t.id === f.itemId) : undefined
    const ref = item?.ref ?? null
    switch (f.who._tag) {
      case "person": {
        const id = f.who.userId
        add(s.members.find((m) => m.userId === id)?.displayName ?? "a person", ref)
        break
      }
      case "worker": {
        const live = item?.activeRunId
          ? s.runs.find((r) => r.id === item.activeRunId && !isRunEnded(r))
          : undefined
        add(live?.worker?.ref ?? "a worker", ref)
        break
      }
      case "agent": {
        const id = f.who.agentId
        add(`@${s.agents.find((a) => a.id === id)?.handle ?? "agent"}`, ref)
        break
      }
      case "decision": {
        const id = f.who.proposalId
        add(`a decision (${s.proposals.find((p) => p.id === id)?.ref ?? "a card"})`, null)
        break
      }
      case "cancelled":
        add("a cancelled item", ref)
        break
      case "nobody":
        add("no one", ref)
        break
    }
  }
  return `waiting on ${[...groups.entries()].map(([who, refs]) => (refs.length > 0 ? `${who} · ${refs.join(", ")}` : who)).join("; ")}`
}

const SECTION_ORDER: ReadonlyArray<MyWorkRow["section"]> = [
  "ready",
  "ready_to_close",
  "in_progress",
  "waiting_on_you",
  "later",
]

/**
 * My work (§5.4): the open items where the user is the person assignee, or the person owner of a container. Sections:
 * Ready for you (their move, todo) · Ready to close (their containers) · In progress (their move, started) · Others are
 * waiting on you (holds others, not yet their move) · Later (the rest, with what they wait on).
 */
export const myWork = (s: ProjectState, userId: UserId, now: number): ReadonlyArray<MyWorkRow> => {
  const projectId = s.project?.id
  if (!projectId) return []
  const rows: MyWorkRow[] = []
  for (const t of s.items) {
    if (!isOpenItem(t)) continue
    const assignee = t.assignee?._tag === "user" && t.assignee.userId === userId
    const ownsContainer = t.owner?._tag === "user" && t.owner.userId === userId && isContainer(s, t)
    if (!assignee && !ownsContainer) continue
    const r = readiness(s, t.id, now)
    const held = holds(s, t, now)
    const section: MyWorkRow["section"] =
      ownsContainer && r._tag === "ready_to_close"
        ? "ready_to_close"
        : r._tag === "with_person" && t.status === "todo"
          ? "ready"
          : r._tag === "with_person" && t.status === "in_progress"
            ? "in_progress"
            : held.length > 0
              ? "waiting_on_you"
              : "later"
    const rev = t.specRev
    const revByUser = rev.by._tag === "user" && rev.by.userId === userId
    rows.push({
      projectId,
      itemId: t.id,
      ref: t.ref,
      title: t.title,
      type: t.type,
      status: t.status,
      section,
      holds: held.length,
      unblocks: held.slice(0, 3).map((h) => h.ref),
      dueAt: t.dueAt,
      overdue: t.dueAt !== null && t.dueAt <= now,
      notBefore: t.notBefore,
      waitingOn:
        section === "later" || section === "waiting_on_you" ? frontierText(s, waitingOn(s, t, now)) : null,
      specChanged:
        rev.n > 1 && !revByUser && rev.at >= now - 3 * DAY
          ? {
              from: rev.n - 1,
              to: rev.n,
              by: actorName(s, rev.by),
              at: rev.at,
            }
          : null,
    })
  }
  const rank = new Map(s.items.map((t) => [t.id, t.rank] as const))
  return rows.sort(
    (a, b) =>
      SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section) ||
      Number(b.overdue) - Number(a.overdue) ||
      (a.dueAt ?? Number.MAX_SAFE_INTEGER) - (b.dueAt ?? Number.MAX_SAFE_INTEGER) ||
      ((rank.get(a.itemId) ?? "") < (rank.get(b.itemId) ?? "") ? -1 : 1),
  )
}

/** The My work badge: Ready for you + Ready to close. */
export const myWorkBadge = (rows: ReadonlyArray<MyWorkRow>): number =>
  rows.filter((r) => r.section === "ready" || r.section === "ready_to_close").length
